/**
 * Stable per-device identifier. Generated once and persisted in localStorage;
 * survives reloads and sessions, resets only when site data is cleared.
 *
 * This is the **experiment unit** for guest-inclusive experiments and the
 * LaunchDarkly guest/device context key. Keeping one id from first visit
 * through signup is what makes variant assignment sticky across login —
 * experiment flags bucket on the `device` context kind, never the user key.
 *
 * Deliberately NOT tied to analytics identity (`distinctIdFor`): the wallet
 * user id identifies a person; this identifies a browser install.
 */
const STORAGE_KEY = 'predictions:device_id';

/** In-memory fallback when localStorage is unavailable (private mode, quota). */
let memoryId: string | null = null;

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getDeviceId(): string {
  if (memoryId) return memoryId;
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) {
      memoryId = existing;
      return existing;
    }
    const fresh = generateId();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    memoryId = fresh;
    return fresh;
  } catch {
    // Storage unavailable — keep a per-tab id so bucketing still works
    // within the session (it just won't survive a reload).
    memoryId = generateId();
    return memoryId;
  }
}

/** Test-only escape hatch. */
export function __resetDeviceIdForTests(): void {
  memoryId = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — nothing persisted to clear
  }
}
